import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError, getApiBase } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { fmtTs } from '../lib/globalTz';

type QuerySource = 'sentinel' | 'defender';

export interface NextBestQueryContext {
  kind: 'incident' | 'entity' | 'ioc' | 'cve' | 'device' | 'generic';
  customerId: string;
  title?: string;
  entityType?: string;
  entityValue?: string;
  incidentId?: string;
  incidentNumber?: string;
  severity?: string;
  provider?: string;
  value?: string;
  type?: string;
  lookback?: string;
  extra?: unknown;
}

export interface NextBestQueryItem {
  id: string;
  title: string;
  description: string;
  source: QuerySource;
  query: string;
  why: string;
  priority: 'High' | 'Medium' | 'Low';
  tags: string[];
  recommendedTimespan: string;
}

interface NextBestQueryResponse {
  ok: true;
  generatedAt: string;
  model: string;
  fallback: boolean;
  context: NextBestQueryContext;
  queries: NextBestQueryItem[];
  warnings: string[];
}

interface NextBestQueryPanelProps {
  customerId: string;
  context: NextBestQueryContext;
  onRunKql?: (query: string) => void;
  incidentRef?: { incidentId?: string; incidentNumber?: string; title?: string };
  compact?: boolean;
  autoGenerate?: boolean;
}

function priorityClass(priority: string): string {
  if (priority === 'High') return 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900';
  if (priority === 'Medium') return 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900';
  return 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200 dark:border-emerald-900';
}

function contextKey(context: NextBestQueryContext): string {
  return JSON.stringify({ kind: context.kind, customerId: context.customerId, entityType: context.entityType, entityValue: context.entityValue, value: context.value, type: context.type, incidentId: context.incidentId, incidentNumber: context.incidentNumber, lookback: context.lookback });
}

function filenameSafe(value: string): string {
  return value.replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 80) || 'next-best-query';
}

export function NextBestQueryPanel({ customerId, context, onRunKql, incidentRef, compact = false, autoGenerate = false }: NextBestQueryPanelProps) {
  const { canCapability } = useAuth();
  const [data, setData] = useState<NextBestQueryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = canCapability('next_best_query', 'generate');
  const canRun = canCapability('next_best_query', 'run_in_hunting') && canCapability('advanced_hunting', 'set_query');
  const canSave = canCapability('next_best_query', 'save_hunting_query');
  const canEvidence = canCapability('next_best_query', 'add_to_case_evidence') && canCapability('case_evidence', 'upload');
  const key = useMemo(() => contextKey({ ...context, customerId }), [context, customerId]);

  useEffect(() => {
    setData(null);
    setError(null);
  }, [key]);

  useEffect(() => {
    if (autoGenerate && canGenerate && !data && !loading) void generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, canGenerate, key, data, loading]);

  async function generate() {
    if (!canGenerate) {
      setError('Permission denied. Enable AI Next Best Query → Generate Queries in Permissions Master.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = await apiFetch<NextBestQueryResponse>('/next-best-query/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, context: { ...context, customerId } }),
      });
      setData(payload);
      toast({ title: 'Next Best Queries generated', description: `${payload.queries.length} query pivot(s) are ready.` });
    } catch (err) {
      const msg = err instanceof ApiError && err.forbidden
        ? 'Permission denied. Enable AI Next Best Query capabilities in Permissions Master.'
        : err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function auditAction(query: NextBestQueryItem, action: string) {
    try {
      await apiFetch('/next-best-query/audit-action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, query, context: { ...context, customerId }, action }),
      });
    } catch { /* best-effort; route is capability-gated but UI action can still proceed */ }
  }

  async function runQuery(query: NextBestQueryItem) {
    if (!canRun || !onRunKql) return;
    onRunKql(query.query);
    void auditAction(query, query.source === 'defender' ? 'next_best_query_opened_in_defender_hunting' : 'next_best_query_opened_in_sentinel_hunting');
  }

  async function saveQuery(query: NextBestQueryItem) {
    setBusyId(`save:${query.id}`);
    setError(null);
    try {
      await apiFetch('/next-best-query/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, query, context: { ...context, customerId } }),
      });
      toast({ title: 'Saved as hunting query', description: query.title });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  async function addToEvidence(query: NextBestQueryItem) {
    if (!incidentRef?.incidentId) return;
    setBusyId(`evidence:${query.id}`);
    setError(null);
    try {
      const body = [
        `// KloudSecure AI Next Best Query`,
        `// Title: ${query.title}`,
        `// Source: ${query.source}`,
        `// Why: ${query.why}`,
        `// Generated: ${data?.generatedAt ?? new Date().toISOString()}`,
        '',
        query.query,
        '',
      ].join('\n');
      const form = new FormData();
      form.append('incidentId', incidentRef.incidentId);
      if (incidentRef.incidentNumber) form.append('incidentNumber', incidentRef.incidentNumber);
      form.append('category', 'KQL Query');
      form.append('description', `AI Next Best Query: ${query.title}. ${query.why}`);
      form.append('visibility', 'internal');
      form.append('file', new Blob([body], { type: 'text/plain;charset=utf-8' }), `${filenameSafe(query.title)}.kql`);
      const resp = await fetch(`${getApiBase()}/case-evidence?customerId=${encodeURIComponent(customerId)}`, { method: 'POST', credentials: 'include', body: form });
      if (!resp.ok) {
        const parsed = await resp.json().catch(() => ({}));
        throw new Error(parsed.message || parsed.error || `Case Evidence upload failed (${resp.status})`);
      }
      await auditAction(query, 'next_best_query_added_to_case_evidence');
      toast({ title: 'Added to Case Evidence', description: query.title });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className={`rounded-2xl border border-indigo-300/40 bg-indigo-950/10 p-4 ${compact ? 'mt-3' : 'mt-4'} space-y-4`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-bold">AI Next Best Query</div>
          <h3 className={`${compact ? 'text-base' : 'text-lg'} font-black text-foreground`}>Context-aware KQL pivots</h3>
          <p className="text-sm text-muted-foreground max-w-4xl">Generates Sentinel and Defender pivots for the current incident/entity/IoC/CVE context. Queries are read-only and can be opened in Advanced Hunting, saved, or attached as case evidence.</p>
        </div>
        <button type="button" onClick={() => void generate()} disabled={loading || !canGenerate} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60">
          {loading ? 'Generating…' : data ? 'Regenerate Queries' : 'Generate Queries'}
        </button>
      </div>

      {!canGenerate && <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">AI Next Best Query is hidden by Permissions Master. Enable Next Best Query → Generate Queries for this role.</div>}
      {error && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200 dark:border-red-900">{error}</div>}

      {data && (
        <>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span>Generated {fmtTs(data.generatedAt)}</span>
            <span>· Model: {data.model}{data.fallback ? ' / deterministic fallback' : ''}</span>
            <span>· Context: {data.context.kind}</span>
          </div>
          <div className="grid gap-3 xl:grid-cols-2">
            {data.queries.map((query) => (
              <div key={query.id} className="rounded-xl border border-border bg-card p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold ${priorityClass(query.priority)}`}>{query.priority}</span>
                      <span className="rounded-full border border-border px-2 py-0.5 text-[11px] font-bold text-muted-foreground">{query.source === 'defender' ? 'Defender XDR' : 'Sentinel'}</span>
                    </div>
                    <h4 className="mt-2 font-black text-foreground">{query.title}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">{query.description}</p>
                  </div>
                  <span className="text-xs text-muted-foreground">{query.recommendedTimespan}</span>
                </div>
                <p className="mt-3 text-xs text-muted-foreground"><strong className="text-foreground">Why:</strong> {query.why}</p>
                <pre className="mt-3 max-h-[210px] overflow-auto rounded-xl border border-border bg-background p-3 text-xs text-muted-foreground whitespace-pre-wrap">{query.query}</pre>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={!canRun || !onRunKql} onClick={() => void runQuery(query)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold hover:bg-muted disabled:opacity-50">{query.source === 'defender' ? 'Run in Defender' : 'Run in Sentinel'}</button>
                  <button type="button" disabled={!canSave || busyId === `save:${query.id}`} onClick={() => void saveQuery(query)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold hover:bg-muted disabled:opacity-50">{busyId === `save:${query.id}` ? 'Saving…' : 'Save as Hunting Query'}</button>
                  <button type="button" disabled={!canEvidence || !incidentRef?.incidentId || busyId === `evidence:${query.id}`} onClick={() => void addToEvidence(query)} className="rounded-lg border border-border px-3 py-1.5 text-xs font-bold hover:bg-muted disabled:opacity-50">{busyId === `evidence:${query.id}` ? 'Adding…' : 'Add to Case Evidence'}</button>
                </div>
                {query.tags.length > 0 && <div className="mt-3 flex flex-wrap gap-1">{query.tags.map((tag) => <span key={tag} className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">{tag}</span>)}</div>}
              </div>
            ))}
          </div>
          {data.warnings.length > 0 && <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-900">{data.warnings.join(' ')}</div>}
        </>
      )}
    </div>
  );
}
